import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as _ from 'lodash'
import isMobile from 'is-mobile'
import { Spring, animated } from 'react-spring/renderprops.cjs'
import {
  OccupancyExtension,
  DiscountExtension,
  BuildingIndexExtension,
  BaseStyleExtension,
  ClassificationExtension,
  CommitmentExtension,
  InventoryExtension,
} from '@wework/floormap-extensions'
import { Text } from '@wework/dieter-ui'
import LoadingPlaceholder from '../Misc/LoadingPlaceholder'
import { FloorMapComponent } from '@wework/react-floormap'
import ErrorPage from '../ErrorPage/ErrorPage'
import SubBar from '../SubBar/SubBar'
import ErrorPageClassification from '../ErrorPage/ErrorPageClassification'
import MapLegendAndControl from '../MapControl/MapLegendAndControl'
import logger from '../../utils/logger'
import { DEFAULT_MAP_STYLE, INTERNAL_ROOMS_MAP_STYLE } from '../../style/style'

import generateSpaceTitleMapping, {
  getTitleWithDeskCount,
} from '../../utils/generateSpaceTitleMapping'
import { Events } from '../../constants/event'
import {
  Floor,
  Space,
  SpaceObjectWithExtensionData,
  SpacePayload,
  SpaceWithExtensionData,
} from '@wework/floormap-sdk'
import Header from '../Header/Header'
import { getUnholdedOnly, isSpaceInteractable } from '../../utils/spaces'
import SpaceHoverPreview from '../Content/SpaceHoverPreview'
import SpacePreview from '../Content/SpacePreview'

import SpaceHoldModal from '../Content/OnHold/SpaceHoldModal'
import FiltersControl from '../FiltersControl/FiltersControl'

import { apercuBold } from '../../assets/fonts/ApercuBold'
import { isDedicatedDeskRoom } from '@wework/floormap-utils'
import FilterManager from '../../managers/FilterManager'
import { NodeConfiguration } from '../../../../floormap-sdk/dist'
import plug from '../../assets/images/legend/plug.png'
import FirstTimeModal from '../BetaModals/FirstTimeModal'
import SideBar from '../Drawer/SideBar'
import { useSidebar } from '../../hoc/withSidebar'
import OfficeNoteEditModal from '../Content/OfficeNote/OfficeNoteEditModal'
import {
  CheckIsDedicatedDesk,
  CheckIsHotDesk,
} from '../../utils/EntitySynonyms'
import WhatsNew from '../WhatsNew'
import Building from '@wework/floormap-sdk/dist/models/Building'
import {
  getLastRead,
  getWNLastVersion,
  setLastRead,
} from '../../utils/whatsnew-utils'

const FIT_CONTENT_PADDING = 60
const FIRST_TIME_KEY = 'isFirstTime'

function InventoryManagerFloorMap(props) {
  const {
    mapBackgroundColor,
    profiling,
    emitUserEvent,
    initialSelectSpace,
  } = props
  const floorMapRef = useRef<any>(null)

  const { openSideBar, isOpen, closeSideBar, sideBarName } = useSidebar()

  const [isFirstTime, setisFirstTime] = useState(
    !localStorage.getItem(FIRST_TIME_KEY)
  )
  const [staticHoverPreview, setStaticHoverPreview] = useState(false)
  const [currentTab, setCurrentTab] = useState('')
  const [selectionColor] = useState('#B2DFFF')

  const [mouseLeaveCanvas, setMouseLeaveCanvas] = useState(false)
  const [holdOpen, setHoldOpen] = useState(false)
  const [noteModalOpen, setNoteModalOpen] = useState(false)
  //Loadings
  const [loading, setLoading] = useState(false)
  const [loadingBuilding, setLoadingBuilding] = useState(false)

  // Header
  const [openFloorsDropDown, setOpenFloorsDropDown] = useState(false)
  const [openLocationsDropDown, setOpenLocationsDropDown] = useState(false)
  const [floorsDropDownEnable, setFloorsDropDownEnable] = useState(true)
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false)

  // Errors
  const [buildingError, setBuildingError] = useState(false)
  const [floorError, setFloorError] = useState(false)
  const [error, setError] = useState(null)

  const [currentBuilding, setCurrentBuilding] = useState({
    uuid: '',
    name: '',
    systemUUID: '',
    floors: [],
  })
  const [currentFloor, setCurrentFloor] = useState({
    uuid: '',
    name: 'Loading...',
  })
  const [spaces, setSpaces] = useState<any>([])
  const [searchResult, setSearchResult] = useState([])
  const [activeSpaces, setActiveSpaces] = useState<any>([])
  const [selectedInternalRooms, setSelectedInternalRooms] = useState<any>([])

  const [multiple, setMultiple] = useState(false)
  const [multipleDeskLink, setMultipleDeskLink] = useState(false)
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false)
  const [azimuthAngle, setAzimuthAngle] = useState(0)
  const [selectInternals, setSelectInternals] = useState(false)
  const [polarAngle, setPolarAngle] = useState(0)
  const [fitContent, setFitContent] = useState({ padding: FIT_CONTENT_PADDING })
  const [selectedSpaces, setSelectedSpaces] = useState<any>([])
  const [highlightedSpaces, setHighlightedSpaces] = useState<any>([])
  const [titleMapping, setTitleMapping] = useState(null)
  const [viewOptions, setViewOptions] = useState({
    spaceList: false,
    deskLayout: false,
    deskCount: false,
    occupancy: true,
    hoverPreview: true,
  })
  const [floorId, setFloorId] = useState(props.floorId || props.initialFloorId)
  const [buildingId, setBuildingId] = useState(
    props.buildingId || props.initialBuildingId
  )
  let previousBuilding: Building | null = null
  let previousFloor: Floor | null = null
  let pendingSelectSpace = initialSelectSpace
  let filterId = 0
  let previousFilter = props.filters || {}
  // useEffect(() => {
  //   floorMapRef.current?.invalidate()
  // }, [ viewOptions])

  // Extensions
  const occupancyExtension = useMemo(
    () =>
      new OccupancyExtension({
        style: viewOptions.occupancy,
        fetchOnUnmapped: true,
      }),
    [viewOptions.occupancy]
  )
  const discountExtension = useMemo(() => new DiscountExtension(), [])
  const styleExtension = useMemo(
    () => new BaseStyleExtension(DEFAULT_MAP_STYLE),
    []
  )
  const buildingIndexExtension = useMemo(
    () => new BuildingIndexExtension({ fetchOnUnmapped: true }),
    []
  )
  const classificationExtension = useMemo(
    () => new ClassificationExtension(),
    []
  )
  const commitmentExtension = useMemo(() => new CommitmentExtension(), [])
  const inventoryExtension = useMemo(() => new InventoryExtension(), [])

  let filterManager = useMemo(
    () => new FilterManager(buildingIndexExtension, occupancyExtension),
    [buildingIndexExtension, occupancyExtension]
  )

  useEffect(() => {
    floorMapRef.current?.setRotation(azimuthAngle, polarAngle)
  }, [azimuthAngle, floorMapRef, polarAngle])

  useEffect(() => {
    if (
      currentTab === 'filters' &&
      !_.isEmpty(props.filters) &&
      (props.filters.search || searchResult.length > 0)
    ) {
      openSideBar('SearchDrawer')
    } else if (sideBarName === 'SearchDrawer') {
      closeSideBar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, props.filters, searchResult])

  useEffect(() => {
    const wnVersion = getWNLastVersion()
    const lastRead = getLastRead()
    if (wnVersion > lastRead) {
      setIsWhatsNewOpen(true)
    }
  }, [])

  useEffect(() => {
    if (_.get(viewOptions, 'spaceList') && selectedSpaces.length === 0) {
      openSideBar('SpaceList')
    }
  }, [viewOptions.spaceList, selectedSpaces, viewOptions, openSideBar])
  useEffect(() => {
    if (sideBarName === 'InternalRooms') {
      setSelectInternals(true)
    } else {
      setSelectInternals(false)
      deselectAllInternals()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideBarName])

  useEffect(() => {
    loadBuilding(props.buildingId, props.floorId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.buildingId, props.floorId])

  useEffect(() => {
    applyFilters(props.filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filters])

  useEffect(() => {
    getOccupancyExtension() &&
      getOccupancyExtension().toggleStyle(viewOptions.occupancy)
    colorBPORooms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOptions.occupancy])

  useEffect(() => {
    // floorMapRef.current?.invalidate()
    // floorMapRef.current?.configureStyle(configureStyle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOptions.deskCount])

  const getOccupancyExtension = () => {
    if (floorMapRef.current) {
      const occ = floorMapRef?.current?.getExtension('reservable')
      return occ
    }
    return null
  }

  useEffect(() => {
    if (spaces.length > 0 && floorId !== previousFloor) {
      colorBPORooms()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, loading, floorId, loadingBuilding])
  useEffect(() => {
    if (
      !selectInternals &&
      styleExtension &&
      spaces.length > 0 &&
      selectedSpaces.length > 0
    ) {
      if (selectedSpaces[0].type !== 'object') {
        floorMapRef.current.applyStyle({
          uuid: selectedSpaces[0]?.uuid,
          style: {
            interactable: true,
            title: {
              text: `${selectedSpaces[0]?.number}`,
              textColor: '#000',
            },
          },
        })
      }
      styleExtension.setStyle(DEFAULT_MAP_STYLE)
      setViewOptions({ ...viewOptions, occupancy: true })
      return
    }
    if (selectInternals && styleExtension) {
      floorMapRef.current.applyStyle({
        uuid: selectedSpaces[0]?.uuid,
        style: {
          interactable: false,
          color: '#003C66',
          title: {
            text: `${selectedSpaces[0]?.number} \n Parent`,
            textColor: '#FFF',
          },
        },
      })

      styleExtension.setStyle(INTERNAL_ROOMS_MAP_STYLE)
      setViewOptions({ ...viewOptions, occupancy: false })
    }
    if (selectedInternalRooms.length > 0) {
      selectedInternalRooms.forEach(room => {
        floorMapRef.current.applyStyle({
          uuid: room.id,
          key: 'selectedIR',
          style: {
            color: '#0077CC',
            title: {
              text: `${room.office_number} \n Internal`,
              textColor: '#FFF',
            },
          },
        })
      })
    }
    // return () => {
    //   cleanup
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInternalRooms, selectInternals])

  const colorBPORooms = () => {
    spaces.forEach(room => {
      if (
        room.inventoryData?.relationships?.length > 0 &&
        room.inventoryData?.relationships[0]?.product_type ===
          'BOOKABLE_PRIVATE_OFFICE'
      ) {
        floorMapRef.current.applyStyle({
          uuid: room.uuid,
          style: {
            color: '#CCCCCC',
          },
        })
      }
    })
  }

  const _applyFilters = async filters => {
    let hasAppliedFilters = false
    const NfilterId = ++filterId
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined) {
        hasAppliedFilters = true
      }
    })

    // Hide spaceList
    setViewOptions({ ...viewOptions, spaceList: false })

    if (!hasAppliedFilters) {
      setSearchResult([])
      setActiveSpaces([])
      setHasAppliedFilters(hasAppliedFilters)
      if (floorMapRef.current) {
        floorMapRef.current.invalidate()
      }
      return
    }
    const result = await filterManager.filter(filters)

    // Added to prevent Race condition
    if (NfilterId !== filterId) return

    setSearchResult(hasAppliedFilters ? result : [])
    setActiveSpaces(hasAppliedFilters ? getActiveSpaces(result) : [])
    setHasAppliedFilters(hasAppliedFilters)

    if (floorMapRef.current) {
      floorMapRef.current.invalidate()
    }

    const cleanedFilters = _.pickBy(filters, _.identity)
    emitUserEvent(Events.FILTER, { filters: cleanedFilters })

    if (cleanedFilters?.search !== previousFilter?.search) {
      if (cleanedFilters.search) {
        emitUserEvent(Events.SEARCH_KEYWORD_CHANGE, {
          value: cleanedFilters.search,
        })
      }
    }

    previousFilter = filters
  }

  let applyFilters = _.debounce(_applyFilters, 200)

  const handleOnFloorMapError = info => {
    if (info.error.message === 'RENDER_CANCELLED') {
      return
    }

    const { error, result } = info

    if (error?.message === 'FLOOR_ID_NOT_FOUND_IN_THE_BUILDING') {
      const lowestFloor = getFirstAvailableFloor(result?.building?.floors)
      if (lowestFloor) {
        // eslint-disable-next-line consistent-return
        return loadBuilding(buildingId, lowestFloor.uuid)
      }
    }

    console.error('[Inventory Manager]: onMapError', info.error)
    props.onLoading({ loading: false })

    const originalError = _.get(info, 'error.originalError')
    if (originalError && originalError.status === 401) {
      setLoading(true)
      setBuildingError(false)
      setFloorError(false)
      setError(info.error)
      setFloorsDropDownEnable(true)
      setCurrentTab('')
      setCurrentBuilding(_.get(info, 'result.building'))

      return
    }

    const building = _.get(info, 'result.building')

    if (info.error.message === 'get_spaces_by_floor_failed') {
      showFloorError(building)
      return
    }

    if (!building) {
      setLoading(true)
      setBuildingError(true)
      setFloorError(false)
      setError(info.error)
      setFloorsDropDownEnable(false)
      setCurrentTab('')
      setCurrentBuilding({
        uuid: '',
        name: '',
        systemUUID: '',
        floors: [],
      })
      return
    }

    if (_.get(info, 'result.building.floors', []).length === 0) {
      showBuildingError(info.result.building)
    } else if (_.get(info, 'result.building.floors', []).length > 0) {
      showFloorError(info.result.building)
    }
  }

  const showFloorError = building => {
    // if there is other floors in this building, then is just the current floor error
    setLoading(false)
    setBuildingError(false)
    setFloorError(true)
    setFloorsDropDownEnable(true)
    setCurrentBuilding(building)
  }

  const showBuildingError = currentBuilding => {
    // there are no floors in the building || there is no building info
    setLoading(true)
    setBuildingError(true)
    setFloorError(false)
    setFloorsDropDownEnable(false)
    setSearchResult([])
    // openSubBar: false,
    setCurrentFloor({ ...currentFloor, name: 'Unknown' })
    setCurrentBuilding(currentBuilding)
  }

  const handleOnViewOptionsChange = (key, value) => {
    let eventName
    if (key === 'hoverPreview') {
      eventName = Events.SHOW_SPACE_HOVER_TOGGLE
    } else if (key === 'occupancy') {
      eventName = Events.VIEW_SPACE_OCCUPANCY
    } else if (key === 'deskLayout') {
      eventName = Events.VIEW_DESK_LAYOUT
    } else if (key === 'deskCount') {
      eventName = Events.VIEW_SPACE_LABELS
    } else if (key === 'spaceList') {
      eventName = Events.VIEW_SPACE_LABELS
    }

    if (eventName) {
      emitUserEvent(eventName, { value })
    }

    const newState = { ...viewOptions, [key]: value }
    setViewOptions(newState)
    occupancyExtension.toggleStyle(viewOptions.occupancy)
  }

  const getFirstAvailableFloor = (floors = []) => {
    for (let i = 0; i < floors.length; i += 1) {
      const floor: Floor = floors[i]

      const buildingIndex = buildingIndexExtension.indexMapByFloorId[floor.uuid]
      if (buildingIndex && buildingIndex.length > 0) {
        return floor
      }
    }

    return floors?.[0]
  }

  const loadBuilding = (bID, fID?) => {
    if (buildingId === bID && floorId === fID) {
      return
    }

    let pendingSelectedTab: string = ''
    if (bID === buildingId) {
      pendingSelectedTab = currentTab === 'select' ? '' : currentTab
    }
    setBuildingId(bID)
    setFloorId(fID)
    setCurrentBuilding({
      name: '',
      uuid: bID,
      systemUUID: '',
      floors: currentBuilding?.floors,
    })
    setCurrentTab(pendingSelectedTab)
    setMultiple(false)
    setCurrentFloor({ name: '', uuid: fID })
    setFloorsDropDownEnable(true)
    setBuildingError(false)
    setFloorError(false)
    setSelectedSpaces([])
    setError(null)
    setLoading(true)
    setLoadingBuilding(true)
    setSearchResult([])
    setActiveSpaces([])
    setLoadingBuilding(true)
  }

  const handleOnTabControlChange = async value => {
    if (value === 'select') {
      // Hide spaceList
      setViewOptions({ ...viewOptions, spaceList: false })
      // setMultiple(!multiple)
      !isOpen ? openSideBar('MultiSelect') : closeSideBar()
    } else if (value === 'download') {
    } else {
      setMultiple(false)
    }

    // if click on the same tab
    if (value === currentTab) {
      setCurrentTab('')
    } else {
      sideBarName === 'SearchDrawer' && closeSideBar()
      setCurrentTab(value)
    }

    // Event Emitting
    let eventName
    const eventValue = currentTab === value
    switch (value) {
      case 'filters':
        eventName = Events.CLICK_APP_BAR_FILTERS
        break
      case 'views':
        eventName = Events.CLICK_APP_BAR_VIEWS
        break
      case 'select':
        eventName = Events.CLICK_APP_BAR_SELECT
        break
      default:
        break
    }

    if (eventName) {
      emitUserEvent(eventName, {
        value: !eventValue,
      })
    }
  }

  const deselectSpace = (space?) => {
    if (multiple) {
      if (!space) {
        return
      }
      setSelectedSpaces(
        selectedSpaces.filter(Boolean).filter(s => s.uuid !== space.uuid)
      )
    } else if (selectInternals) {
      if (!space) {
        return
      }
    } else {
      setSelectedSpaces([])
    }
  }

  const renderSubBarContent = () => {
    if (currentTab === 'select') {
      return (
        <Text>
          {props.multipleSelectDescriptions
            ? props.multipleSelectDescriptions
            : 'You can select multiple rooms to put spaces on hold in bulk.'}
        </Text>
      )
    }
    if (currentTab === 'filters') {
      return (
        <FiltersControl
          onFilterChange={filters => {
            setFiltersState(filters)
          }}
          filters={props.filters}
          toggleResultList={() =>
            sideBarName === 'SearchDrawer'
              ? closeSideBar()
              : openSideBar('SearchDrawer')
          }
          hasAppliedFilters={hasAppliedFilters}
          isSearchDrawerOpen={sideBarName === 'SearchDrawer'}
          loading={loading}
        />
      )
    }

    return ''
  }

  const selectSpace = space => {
    if (space.subType === 'HALLWAY') {
      return
    }
    emitUserEvent(Events.SPACE_CLICK, { space, multiple })

    if (multiple) {
      const existing = selectedSpaces.find(s => s.uuid === space.uuid)
      if (existing) {
        return
      }
      !isOpen && openSideBar('MultiSelect')
      setSelectedSpaces(selectedSpaces.concat([space]))
    } else if (selectInternals) {
      const existing = selectedInternalRooms.find(s => s.id === space.uuid)
      if (existing) {
        deselectInternalRoom(space)
        return
      }
      setSelectedInternalRooms(
        selectedInternalRooms.concat([{ ...space.inventoryData }])
      )
    } else if (multipleDeskLink) {
      if (space.subType === 'CHAIR') {
        const existing = selectedSpaces.find(s => s.id === space.uuid)
        if (existing) {
          deselectSpace(space)
          return
        }
        setSelectedSpaces(selectedSpaces.concat([{ ...space }]))
      }
    } else {
      emitUserEvent(Events.SPACE_DETAILS, {
        space:
          _.get(space, 'number') ||
          _.get(space, 'index.number') ||
          _.get(space, 'reservable.officeNumber'),
      })
      setSelectedSpaces([space])
      openSideBar('InventoryDrawer')
    }

    dehighlightSpace()
  }

  const handleClickSpace = event => {
    const { data } = event

    if (data) {
      const selectedRoom = data

      const existing = selectedSpaces.find(s => s.uuid === selectedRoom.uuid)
      if (existing) {
        if (selectedRoom.uuid === selectedSpaces[0].uuid) {
          // this.closeInternalRoomsSelectMode()
        }
        deselectSpace(selectedRoom)
        highlightSpace(data)
        return
      }
      // if (selectedRoom.type === 'object' && multipleDeskLink) {
      //   selectSpace(selectedRoom)
      //   return
      // }
      if (!isSpaceInteractable(selectedRoom, floorMapRef.current)) {
        deselectSpace()
        return
      }
      if (selectedRoom.type === 'object' && multiple) {
        return
      }
      selectSpace(selectedRoom)
    } else {
      if (multiple) {
        return
      }
      deselectSpace()
    }
  }

  const handleOnChangeFloor = newFloor => {
    // do not change floor if is the same uuid
    if (currentFloor.uuid === newFloor.uuid) {
      return
    }

    // If state and props is the same
    // meaning this is in uncontrolled mode
    if (floorId !== props.floorId) {
      loadBuilding(buildingId, newFloor.uuid)
    }

    if (props.onChangeFloor) {
      props.onChangeFloor(newFloor)
    }
  }

  const handleMouseOverSpaceList = space => {
    highlightSpace(space)
    setStaticHoverPreview(true)
  }

  const handleMouseOutSpaceList = () => {
    dehighlightSpace()
    setStaticHoverPreview(false)
  }

  const handleOnMultiSelect = () => {
    setViewOptions({ ...viewOptions, spaceList: false })
    setMultiple(!multiple)

    if (isOpen) {
      closeSideBar()
      setSelectedSpaces([])
    } else {
      openSideBar('MultiSelect')
    }
  }

  const handleOnChangeLocation = async buildingPayload => {
    const nextBuildingId = buildingPayload?.uuid

    if (
      currentBuilding.uuid === nextBuildingId ||
      currentBuilding.systemUUID === nextBuildingId
    ) {
      // ignore if it is the same building
      return
    }

    // We are uncontrolled component for building Id
    if (buildingId !== props.buildingId) {
      loadBuilding(nextBuildingId)
    }

    if (props.onBuildingChange) {
      logger.deprecate('props.onBuildingChange', 'props.onChangeBuilding')
      props.onBuildingChange(buildingPayload)
    }

    if (props.onChangeBuilding) {
      props.onChangeBuilding(buildingPayload)
    }
  }

  const onSelectProfileMenu = value => {
    if (value === 'logout') {
      if (props.onSignOut) {
        props.onSignOut()
      }
      return
    }
    if (value === 'feedback') {
    }
    if (value === 'whatsnew') {
      setIsWhatsNewOpen(true)
    }
  }

  const renderHeader = () => {
    return (
      <Header
        currentBuilding={currentBuilding}
        currentFloor={currentFloor}
        floorList={currentBuilding.floors}
        loading={loading}
        buildingError={buildingError}
        floorError={floorError}
        openFloorsDropDown={openFloorsDropDown}
        handleFloorsDropDownToggle={() =>
          setOpenFloorsDropDown(!openFloorsDropDown)
        }
        handleFloorsDropDownClose={() => setOpenFloorsDropDown(false)}
        openLocationsDropDown={openLocationsDropDown}
        handleLocationsDropDownState={open => setOpenLocationsDropDown(open)}
        FloorsDropDownEnable={floorsDropDownEnable}
        onChangeFloor={handleOnChangeFloor}
        onChangeLocation={handleOnChangeLocation}
        onSearchChange={handleSearchChange}
        searchText={props.filters.search || ''}
        profileMenuProps={{
          onSelect: e => onSelectProfileMenu(e),
        }}
        onTabChange={handleOnTabControlChange}
        currentTab={currentTab}
        hasAppliedFilters={hasAppliedFilters}
        borderBottom={!!currentTab}
        spaces={spaces}
        allowUnMappedBuilding={props.allowUnMappedBuilding}
        isClassification={_.get(props.features, 'isClassification', false)}
        emitUserEvent={emitUserEvent}
        setLoading={setLoading}
        viewOptions={viewOptions}
        onMultiSelectClick={handleOnMultiSelect}
        multipleSelected={multiple}
        handleOnViewOptionsChange={handleOnViewOptionsChange}
      />
    )
  }

  const configureStyle = (space, prevConfig) => {
    if (props.configureStyle) {
      return props.configureStyle(space, activeSpaces, floorMapRef)
    }
    let config: NodeConfiguration = { interactable: true }

    if (space.subType === 'HALLWAY') {
      return {}
    }

    if (space.subType === 'TABLE' || space.subType === 'DESK') {
      config.interactable = false
      if (!space.parentUUID || space.parentUUID === space.spaceUUID) {
        const parentSpace = floorMapRef.current?.getSpaceById(space.spaceUUID)
        if (
          isDedicatedDeskRoom(parentSpace?.subType, parentSpace?.roomTypeUUID)
        ) {
          config.interactable = true
          config.visible = true
        }
      }
    }

    if (space.subType === 'CHAIR' || space.subType === 'SEAT') {
      const parentSpace = floorMapRef?.current?.getSpaceById(space.spaceUUID)
      if (
        isDedicatedDeskRoom(parentSpace?.subType, parentSpace?.roomTypeUUID)
      ) {
        config.interactable = true
        config.visible = true
      } else {
        config.interactable = false
      }
    }

    if (space.reservable) {
      config.interactable = true
    }
    if (activeSpaces) {
      if (!activeSpaces.includes(space.uuid)) {
        config = {
          ...config,
          color: '#cccccc',
          texture: {},
          outline: '#979797',
        }
        // config.interactable = false
      } else {
        config.interactable = true
        if (
          !space.reservable &&
          (space.programType.toUpperCase() === 'MEET' ||
            space.subType.toUpperCase() === 'PHONE BOOTH' ||
            space.type === 'object')
        ) {
          config.color = '#f0f8fa'
        }
      }
    }
    if (space.type === 'object' && space.subType === 'FURNITURE') {
      config.visible = false
    }
    config.outline = '#000'
    if (space.type === 'space' && prevConfig.title.text) {
      config.title = {
        icon:
          !space.reservable &&
          !prevConfig.title.icon &&
          !CheckIsHotDesk(space.subType.toUpperCase()) &&
          !CheckIsDedicatedDesk(space.subType.toUpperCase())
            ? plug
            : prevConfig.title.icon,
        text: prevConfig?.title?.text,
        textColor: '#000',
        font: JSON.stringify(apercuBold),
      }
    }
    if (
      viewOptions.deskCount &&
      space.type === 'space' &&
      prevConfig.title.text
    ) {
      config.title = {
        text: getTitleWithDeskCount(space),
        textColor: '#000',
        font: JSON.stringify(apercuBold),
      }
    }

    return config
  }

  const handleSearchChange = e => {
    const value = e.target.value
    setCurrentTab('filters')
    setFiltersState({
      search: value !== '' ? value : undefined,
    })
  }

  const setFiltersState = options => {
    setSelectedSpaces([])
    setMultiple(false)

    if (props.onFilterChange) {
      const compactObject = _.pickBy(
        {
          ...props.filters,
          ...options,
        },
        _.identity
      )
      props.onFilterChange(compactObject)
    }

    const allKeys = Object.keys(options)
    const key = allKeys.length > 0 ? allKeys[0] : null
    if (!key) {
      return
    }

    const value = options[key]
    if (typeof value !== 'undefined') {
      emitUserEvent(Events.ADD_FILTER, {
        key,
        value,
      })
    }
  }

  const handleRotate = degree => {
    setAzimuthAngle(azimuthAngle + degree)
  }

  const handleResetCamera = () => {
    setAzimuthAngle(0)
    setPolarAngle(0)
    setFitContent({ padding: FIT_CONTENT_PADDING })
  }

  const renderMapControl = () => {
    return (
      <MapLegendAndControl
        //@ts-ignore
        disabled={loading || buildingError}
        onRotate={handleRotate}
        onResetCamera={handleResetCamera}
        buildingId={buildingId}
        onLegendToggle={() => {
          sideBarName === 'Legend' ? closeSideBar() : openSideBar('Legend')
        }}
      />
    )
  }

  const highlightSpace = (
    space: Space | SpaceObjectWithExtensionData | SpaceWithExtensionData
  ) => {
    if (isMobile({ tablet: true })) {
      return
    }
    if (space.subType === 'HALLWAY') {
      return
    }
    const floorMap = floorMapRef.current
    const spacesToHighlight: any[] = []

    spacesToHighlight.push(space)

    const targetSpace = space

    emitUserEvent(Events.SPACE_HOVER, { space: targetSpace })
    const reservable = _.get(targetSpace, 'reservable')
    if (reservable) {
      const { matchedPhysicalUUIDs } = reservable
      matchedPhysicalUUIDs.forEach(muuid => {
        const matchedSpace: SpacePayload | undefined = floorMap?.getSpaceById(
          muuid
        )
        if (matchedSpace) {
          spacesToHighlight.push(matchedSpace)
        }
      })
    }
    setHighlightedSpaces(spacesToHighlight)
  }

  const renderLoader = loading => {
    if (loading && _.get(props.features, 'loader') !== false) {
      return <LoadingPlaceholder background="#fff" />
    }
    return null
  }

  const onMouseLeaveMapElement = () => {
    dehighlightSpace()
    setMouseLeaveCanvas(true)
  }

  const onMouseEnterMapElement = () => {
    if (mouseLeaveCanvas) {
      setMouseLeaveCanvas(false)
    }
  }

  const handleOnFloorMapLoad = ({ buildingId, floorId }) => {
    props.onLoading({ loading: true })
  }

  const handleMouseLeave = () => {
    dehighlightSpace()
  }

  const handleHoverSpace = event => {
    const { data } = event
    if (data) {
      const selectedRoom = data
      if (!isSpaceInteractable(selectedRoom, floorMapRef.current)) {
        return
      }
      const existingSelection = selectedSpaces.find(
        s => s.uuid === selectedRoom.uuid
      )
      if (existingSelection) {
        return
      }
      setStaticHoverPreview(true)
      highlightSpace(selectedRoom)
    } else {
      dehighlightSpace()
    }
  }

  const getSpaceData = space => {
    if (!space) {
      return null
    }
    return space
  }

  const renderHoverPreview = () => {
    return (
      <SpaceHoverPreview
        show={viewOptions.hoverPreview && Boolean(_.first(highlightedSpaces))}
        staticHoverPreview={staticHoverPreview}
        mouseLeaveCanvas={mouseLeaveCanvas}
      >
        <SpacePreview
          space={getSpaceData(_.first(highlightedSpaces))}
          renderSpacePreviewContents={props.renderSpacePreviewContents}
        />
      </SpaceHoverPreview>
    )
  }

  const handleSpaceDataChange = event => {
    const { data, dataType } = event

    if (data && dataType === 'building') {
      if (_.get(currentBuilding, 'uuid') === data.uuid) {
        setCurrentBuilding(data)
      }
      return
    }

    if (!data || selectedSpaces.length === 0) return

    const existingIndex = _.findIndex(selectedSpaces, ['uuid', data.uuid])
    if (!(existingIndex < 0)) {
      return
    }

    // eslint-disable-next-line react/no-access-state-in-setstate
    const newSelectedSpaces = [...selectedSpaces]
    newSelectedSpaces[existingIndex] = data
    setSelectedSpaces(newSelectedSpaces)
  }

  const handleSearchResultClick = resultItem => {
    const floorUUID = resultItem.floorUUID || resultItem.floor_uuid

    if (currentFloor.uuid !== floorUUID) {
      pendingSelectSpace = resultItem
      setLoading(true)
      setFloorId(floorUUID)

      dehighlightSpace()
      return
    }

    const uuid =
      _.get(resultItem, 'index.uuid') || resultItem.uuid || resultItem.id
    // Result item is not fully space data
    // - If it is chair, we try to ge table to highlight table instead of chair itself
    const space = floorMapRef.current?.getSpaceById(uuid)
    // - If we can find space, probably it is different floor
    if (!space) {
      return
    }

    selectSpace(space)
  }

  const handleSearchResultMouseOut = () => {
    dehighlightSpace()
    setStaticHoverPreview(false)
  }

  const handelSearchResultHover = resultItem => {
    const uuid = _.get(resultItem, 'index.uuid') || resultItem.uuid
    // Result item is not fully space data
    // - If it is chair, we try to ge table to highlight table instead of chair itself
    const space:
      | SpaceWithExtensionData
      | SpaceObjectWithExtensionData
      | undefined = floorMapRef.current?.getSpaceById(uuid)
    // - If we can find space, probably it is different floor
    if (space) {
      highlightSpace(space)
    } else {
      highlightSpace(resultItem)
    }

    setStaticHoverPreview(true)
  }

  const dehighlightSpace = () => {
    setHighlightedSpaces([])
  }

  const getActiveSpaces = searchResult => {
    if (searchResult.length === 0) {
      return new Set([])
    }

    const result = _.flatMap(searchResult, s => {
      const allowedSpaces: any = []
      allowedSpaces.push(s.uuid)
      return allowedSpaces
    })
    return new Set(result)
  }

  const closeInternalRoomsSelectMode = () => {
    setSelectInternals(false)
    deselectAllInternals()
  }
  const deselectAllInternals = () => {
    selectedInternalRooms.forEach(room => {
      floorMapRef.current.resetStyle({ id: room.id })
    })
    setSelectedInternalRooms([])
  }
  const deselectInternalRoom = space => {
    if (selectInternals) {
      if (!space) {
        return
      }

      const spaceID = space.id || space.uuid
      setSelectedInternalRooms(
        selectedInternalRooms.filter(Boolean).filter(s => s.id !== spaceID)
      )
      floorMapRef.current.revertStyle({ id: spaceID, key: 'selectedIR' })
    } else {
      setSelectedInternalRooms([])
    }
  }

  const handleOnFloorMapRender = result => {
    props.onLoading({ loading: false })

    if (!result.building || _.get(result, 'building.floors', []).length === 0) {
      // if the building doesn't have any floors at all
      // show no floor error
      showBuildingError(result.building)
      return
    }

    const spaces = _.sortBy(result.spaces || [], ['number'])

    // This case is not the floor error, since the floor error will be invoke onError instead
    // This will be happened when we pass only buildingId and floormap successfully loaded data
    // -
    // If there is no floor, choose the first available floor
    if (!result.floor) {
      if (profiling) {
        profiling({ ...result })
      }

      const firstAvailableFloor = getFirstAvailableFloor(
        result.building?.floors
      )
      if (firstAvailableFloor) {
        setFloorId(firstAvailableFloor.uuid)
        return
      }

      if (result.building.floors && result.building.floors.length > 0) {
        setFloorId(result.building.floors[0].uuid)
        return
      }
      // all the floors doesn't have buildingIndex
      // show no floor error too
      showBuildingError(result.building)
      return
    }

    if (previousBuilding?.uuid !== result.building.uuid) {
      if (!previousBuilding) {
        // First load building
        emitUserEvent(Events.BUILDING_LOADED, {
          building_uuid: result.building.uuid,
          location_uuid: result.building.systemUUID,
          location_name: result.building.name,
        })
      } else {
        // Emit Building Change
        emitUserEvent(Events.BUILDING_CHANGE, {
          building_uuid: result.building.uuid,
          location_uuid: result.building.systemUUID,
          location_name: result.building.name,
        })
      }
    }

    // Same building + Different Floor = Floor Change
    if (
      previousBuilding?.uuid === result.building.uuid &&
      previousFloor?.uuid !== result.floor.uuid
    ) {
      // Emit Floor Change Event
      emitUserEvent(Events.FLOOR_CHANGE, {
        building_uuid: result.building.uuid,
        location_uuid: result.building.systemUUID,
        location_name: result.building.name,
      })
    }

    previousBuilding = result.building
    previousFloor = result.floor

    // Remove all key with falsely value
    const hasFilters = _.size(_.pickBy(props.filters, _.identity)) > 0
    setCurrentBuilding(result.building)
    setCurrentFloor(result.floor)
    setLoading(false)
    setSpaces(spaces)
    setBuildingError(false)
    setFloorError(false)
    setTitleMapping(generateSpaceTitleMapping(spaces))
    setLoadingBuilding(false)
    setFloorsDropDownEnable(true)
    setCurrentTab(hasFilters ? 'filters' : currentTab)
    if (hasFilters) {
      applyFilters(props.filters)
    }

    if (pendingSelectSpace) {
      // PendingSelectSpace can be object with uuid or string uuid
      const uuid = pendingSelectSpace.uuid || pendingSelectSpace
      const selectingSpace = floorMapRef?.current?.getSpaceById(uuid)
      if (selectingSpace) {
        // selectingSpace = this.pendingSelectSpace
        selectSpace(selectingSpace)
      }
    }
    pendingSelectSpace = undefined

    if (profiling) {
      profiling({ ...result })
    }
  }

  if (buildingError) {
    return (
      <ErrorPageClassification
        //@ts-ignore
        buildingError
        currentBuilding={currentBuilding}
        title={'Inventory Manager'}
      />
    )
  }

  if (floorError) {
    return (
      <ErrorPageClassification
        //@ts-ignore
        floorError
        currentBuilding={currentBuilding}
        currentFloor={currentFloor}
        title={'Inventory Manager'}
      />
    )
  }

  if (error) {
    return (
      <ErrorPage
        locationName={currentBuilding ? currentBuilding.name : undefined}
      />
    )
  }

  return (
    <FloorMapComponent
      {...props}
      deskLayout={viewOptions.deskLayout}
      deskInteractable
      selectedSpaces={selectedSpaces}
      highlightedSpaces={highlightedSpaces}
      onMouseOver={handleHoverSpace}
      onMouseLeave={handleMouseLeave}
      onClickSpace={handleClickSpace}
      onDataChange={handleSpaceDataChange}
      extensions={[
        styleExtension,
        occupancyExtension,
        discountExtension,
        buildingIndexExtension,
        classificationExtension,
        commitmentExtension,
        inventoryExtension,
      ]}
      buildingId={buildingId}
      floorId={floorId}
      onLoad={handleOnFloorMapLoad}
      didRender={handleOnFloorMapRender}
      onError={handleOnFloorMapError}
      selectionColor={selectionColor}
      initialAzimuthAngle={azimuthAngle}
      initialPolarAngle={polarAngle}
      initialFitContent={fitContent}
      floorMapRef={floorMapRef}
      spaceTitleMapping={viewOptions.deskCount ? titleMapping : {}}
      backgroundColor={mapBackgroundColor}
      configureStyle={configureStyle}
      renderOptions={{
        autoLoadFloor: false,
      }}
    >
      {({ loading }, mapElement) => {
        return (
          <>
            {renderHeader()}
            <WhatsNew
              open={isWhatsNewOpen}
              onClose={() => {
                setIsWhatsNewOpen(false)
                setLastRead()
              }}
            />
            <OfficeNoteEditModal
              open={noteModalOpen}
              floorMap={floorMapRef.current}
              handler={() => setNoteModalOpen(!noteModalOpen)}
              space={selectedSpaces[0]}
              onHoldClick={() => setHoldOpen(true)}
            />

            <FirstTimeModal
              open={isFirstTime}
              onClose={() => {
                setisFirstTime(false)
                localStorage.setItem(FIRST_TIME_KEY, 'NO')
              }}
            />
            {holdOpen && (
              <SpaceHoldModal
                floorMap={floorMapRef.current}
                open={holdOpen}
                handler={e => setHoldOpen(e)}
                spaces={
                  multiple ? getUnholdedOnly(selectedSpaces) : selectedSpaces
                }
              />
            )}

            <div style={{ position: 'relative', flex: 1 }}>
              <SubBar open={!!currentTab && currentTab !== 'download'}>
                {renderSubBarContent()}
              </SubBar>
            </div>
            {/* {renderScale()} */}
            <div
              style={{
                position: 'absolute',
                bottom: 32,
                left: 36,
                zIndex: 1000,
                display: 'flex',
              }}
            >
              {renderMapControl()}
            </div>
            {renderHoverPreview()}
            <SideBar
              allSpaces={spaces}
              currentBuilding={currentBuilding}
              currentBuildingUUID={currentBuilding.uuid}
              searchResult={searchResult}
              multiple={multiple}
              handleSearchResultClick={handleSearchResultClick}
              handleMouseOutSpaceList={handleMouseOutSpaceList}
              handleMouseOverSpaceList={handleMouseOverSpaceList}
              multipleSetter={setMultiple}
              multiDeskSetter={setMultipleDeskLink}
              multipleDeskLink={multipleDeskLink}
              noteModalHandler={setNoteModalOpen}
              spaces={selectedSpaces}
              selectedInternals={selectedInternalRooms}
              setSelectedInternalRooms={setSelectedInternalRooms}
              zIndex={2000}
              offsetTop={currentTab === 'filters' ? 145 : 80}
              onHoldHandler={e => setHoldOpen(e)}
              spaceSetter={e => {
                setSelectedSpaces(e)
              }}
              currentFloorUUID={currentFloor ? currentFloor.uuid : ''}
              deselectAllInternals={deselectAllInternals}
              deselectInternalRoom={deselectInternalRoom}
            />
            <Spring
              from={{ opacity: 0 }}
              to={{ opacity: loading ? 0 : 1 }}
              config={{ duration: 200 }}
              native
            >
              {props => {
                return (
                  <animated.div
                    style={props}
                    onMouseLeave={onMouseLeaveMapElement}
                    onMouseEnter={onMouseEnterMapElement}
                    onMouseMove={onMouseEnterMapElement}
                  >
                    {mapElement}
                  </animated.div>
                )
              }}
            </Spring>
            {renderLoader(loading && !buildingError && !floorError)}
            {/* </div> */}
          </>
        )
      }}
    </FloorMapComponent>
  )
}

export default InventoryManagerFloorMap
